import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  DocumentTextIcon, 
  TrashIcon, 
  ArrowDownTrayIcon,
  XMarkIcon,
  PaperClipIcon,
  CalendarDaysIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';
import { usePermissions } from '../context/PermissionsContext';

interface Document {
  id?: number;
  client_id: number;
  document_type: string;
  description: string;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  created_at?: string;
}

interface DocumentType {
  id: number;
  name: string;
  created_at?: string;
  updated_at?: string;
}

interface ClientPersonalDocumentsProps {
  clientId: string;
  onDocumentChange?: () => void;
}

const API_URL = process.env.REACT_APP_API_URL || '';

const ClientPersonalDocuments: React.FC<ClientPersonalDocumentsProps> = ({ clientId, onDocumentChange }) => {
  const { can } = usePermissions();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loadingDocumentTypes, setLoadingDocumentTypes] = useState(false);

  // Fetch document types from API
  useEffect(() => {
    fetchDocumentTypes();
  }, []);

  // Fetch existing documents when clientId changes
  useEffect(() => {
    if (clientId) {
      fetchDocuments();
    }
  }, [clientId]);

  const fetchDocumentTypes = async () => {
    setLoadingDocumentTypes(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      console.log('Fetching document types...');
      
      const response = await axios.get(
        `${API_URL}/api/document-types`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('Document types API response:', response.data);
      
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        setDocumentTypes(response.data.data);
      } else if (response.data && Array.isArray(response.data)) {
        setDocumentTypes(response.data);
      } else {
        console.error('Unexpected document types response format:', response.data);
        // Fallback to default types if API fails
        setDocumentTypes([
          { id: 1, name: 'Company Registration Documents' },
          { id: 2, name: 'Identification Document' },
          { id: 3, name: 'Other' }
        ]);
      }
      
    } catch (error: any) {
      console.error('Error fetching document types:', error);
      console.error('Error response:', error.response);
      
      // Fallback to default document types
      setDocumentTypes([
        { id: 1, name: 'Company Registration Documents' },
        { id: 2, name: 'Identification Document' },
        { id: 3, name: 'Other' }
      ]);
      
      setError('Failed to load document types. Using default types.');
    } finally {
      setLoadingDocumentTypes(false);
    }
  };

  const fetchDocuments = async () => {
    if (!clientId) return;
    
    setLoadingDocuments(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      console.log('Fetching documents for clientId:', clientId);
      console.log('API URL:', `${API_URL}/api/client-documents?clientId=${clientId}`);

      const response = await axios.get(
        `${API_URL}/api/client-documents?clientId=${clientId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('Documents API response:', response.data);
      console.log('Response type:', typeof response.data);
      console.log('Is array?', Array.isArray(response.data));
      console.log('Number of documents:', Array.isArray(response.data) ? response.data.length : 'Not an array');
      
      // Handle different response formats
      if (response.data && Array.isArray(response.data)) {
        setDocuments(response.data);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setDocuments(response.data.data);
      } else if (response.data && typeof response.data === 'object') {
        // Try to extract array from object
        const dataArray = Object.values(response.data).find(val => Array.isArray(val));
        if (dataArray && Array.isArray(dataArray)) {
          setDocuments(dataArray);
        } else {
          console.error('Could not find documents array in response:', response.data);
          setDocuments([]);
        }
      } else {
        console.error('Unexpected response format:', response.data);
        setDocuments([]);
      }
      
    } catch (error: any) {
      console.error('Error fetching documents:', error);
      console.error('Error response:', error.response);
      setError('Failed to load documents: ' + (error.response?.data?.message || error.message));
      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    
    // Auto-fill description if empty
    if (file && !description) {
      setDescription(file.name.split('.')[0].replace(/[_-]/g, ' '));
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    if (!documentType) {
      setError('Please select a document type');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      // Get the document type name for the selected ID
      const selectedDocType = documentTypes.find(dt => dt.id.toString() === documentType);
      if (!selectedDocType) {
        throw new Error('Selected document type not found');
      }

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('clientId', clientId);
      formData.append('documentType', selectedDocType.name); // Use the document type name
      formData.append('description', description);

      console.log('Uploading document...');
      console.log('Form data:', {
        clientId,
        documentType: selectedDocType.name,
        description,
        fileName: selectedFile.name,
        fileSize: selectedFile.size
      });

      const response = await axios.post(
        `${API_URL}/api/client-documents/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(progress);
            }
          },
        }
      );

      console.log('Upload response:', response.data);
      setSuccess('Document uploaded successfully!');
      setSelectedFile(null);
      setDocumentType('');
      setDescription('');
      setUploadProgress(0);
      
      // Refresh documents list
      setTimeout(() => {
        fetchDocuments();
      }, 500);

      // ✅ Notify parent so the Notes tab re-fetches audit logs
      onDocumentChange?.();
      
    } catch (error: any) {
      console.error('Error uploading document:', error);
      console.error('Error response:', error.response?.data);
      setError(error.response?.data?.message || 'Failed to upload document');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      console.log('Downloading document:', doc.id);

      const response = await axios.get(
        `${API_URL}/api/client-documents/download/${doc.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create blob link and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error: any) {
      console.error('Error downloading document:', error);
      setError('Failed to download document');
    }
  };

  const handleView = async (doc: Document) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
        `${API_URL}/api/client-documents/download/${doc.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const file = new Blob([response.data], { type: doc.mime_type });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');
      
      // Clean up the URL object after a delay to ensure the tab loads
      setTimeout(() => window.URL.revokeObjectURL(fileURL), 1000);
      
    } catch (error: any) {
      console.error('Error viewing document:', error);
      setError('Failed to open document');
    }
  };

  const handleDelete = async (documentId: number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    setIsDeleting(documentId);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      await axios.delete(
        `${API_URL}/api/client-documents/${documentId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess('Document deleted successfully!');
      fetchDocuments();

      // ✅ Notify parent so the Notes tab re-fetches audit logs
      onDocumentChange?.();

    } catch (error: any) {
      console.error('Error deleting document:', error);
      setError('Failed to delete document');
    } finally {
      setIsDeleting(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDocumentIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('image')) return '🖼️';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    return '📎';
  };

  const getDocumentTypeLabel = (type: string) => {
    // Try to find the document type by name
    const found = documentTypes.find(dt => dt.name === type);
    return found ? found.name : type.replace(/_/g, ' ').toUpperCase();
  };

  // Convert document types to options format for the select dropdown
  const documentTypeOptions = documentTypes.map(dt => ({
    value: dt.id.toString(),
    label: dt.name
  }));

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold text-gray-800">Client Documents</h3>
          <p className="text-sm text-gray-500 mt-1">Manage all client documents in one place</p>
        </div>
        <div className="bg-blue-50 px-3 py-1 rounded-full border border-blue-100">
          <span className="text-sm font-medium text-blue-700">Client ID: {clientId}</span>
        </div>
      </div>

      {/* Upload Form */}
      {can('documents', 'write') && (
      <div className="bg-gradient-to-br from-white to-blue-50 p-6 rounded-xl shadow-lg border border-blue-100">
        <div className="flex items-center mb-6">
          <div className="bg-blue-100 p-2 rounded-lg mr-3">
            <DocumentIcon className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h4 className="text-lg font-semibold text-gray-800">Upload New Document</h4>
            <p className="text-sm text-gray-500">Add new files to client's document repository</p>
          </div>
        </div>
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XMarkIcon className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{success}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleUpload} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Document Type */}
            <div className="space-y-2">
              <label htmlFor="documentType" className="block text-sm font-medium text-gray-700">
                Document Type <span className="text-red-500">*</span>
              </label>
              {loadingDocumentTypes ? (
                <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
                  <svg className="animate-spin w-5 h-5 text-gray-400 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-gray-500">Loading document types...</span>
                </div>
              ) : (
                <select
                  id="documentType"
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors"
                  required
                  disabled={documentTypeOptions.length === 0}
                >
                  <option value="" className="text-gray-400">
                    {documentTypeOptions.length === 0 ? 'No document types available' : 'Select Document Type'}
                  </option>
                  {documentTypeOptions.map((type) => (
                    <option key={type.value} value={type.value} className="text-gray-800">
                      {type.label}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <input
                type="text"
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter document description"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors"
              />
            </div>
          </div>

          {/* File Input */}
          <div className="space-y-2">
            <label htmlFor="file" className="block text-sm font-medium text-gray-700">
              Select File <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-400 transition-colors">
              <div className="space-y-2 text-center">
                <PaperClipIcon className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="file"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                  >
                    <span>Upload a file</span>
                    <input
                      id="file"
                      type="file"
                      onChange={handleFileSelect}
                      className="sr-only"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt"
                      required
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  PDF, JPG, PNG, DOC, DOCX, TXT up to 10MB
                </p>
                {selectedFile && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center">
                      <DocumentTextIcon className="w-5 h-5 text-blue-500 mr-2" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(selectedFile.size)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Upload Progress */}
          {isLoading && uploadProgress > 0 && (
            <div className="space-y-3">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Uploading...</span>
                <span className="font-medium">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Upload and Cancel Buttons */}
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-2">
            <button
              type="submit"
              disabled={isLoading || documentTypeOptions.length === 0 || loadingDocumentTypes}
              className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </>
              ) : (
                <>
                  <DocumentTextIcon className="w-5 h-5 mr-2" />
                  {documentTypeOptions.length === 0 ? 'No Document Types Available' : 'Upload Document'}
                </>
              )}
            </button>
            
            <button
              type="button"
              onClick={() => {
                setSelectedFile(null);
                setDocumentType('');
                setDescription('');
                setUploadProgress(0);
                setError('');
                setSuccess('');
              }}
              disabled={isLoading}
              className="flex-1 bg-gradient-to-r from-gray-500 to-gray-600 text-white py-3 px-4 rounded-lg hover:from-gray-600 hover:to-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg"
            >
              <XMarkIcon className="w-5 h-5 mr-2" />
              Cancel
            </button>
          </div>
        </form>
      </div>
      )}

      {/* Documents List Section */}
      <div className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-xl shadow-lg border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h4 className="text-lg font-semibold text-gray-800">Uploaded Documents</h4>
            <p className="text-sm text-gray-500 mt-1">
              {documents.length} document{documents.length !== 1 ? 's' : ''} uploaded
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={fetchDocumentTypes}
              className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-lg transition-colors"
              disabled={loadingDocumentTypes}
              title="Refresh document types"
            >
              {loadingDocumentTypes ? (
                <svg className="animate-spin w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
              {loadingDocumentTypes ? 'Refreshing...' : 'Refresh Types'}
            </button>
            <button
              onClick={fetchDocuments}
              className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-lg transition-colors"
              disabled={loadingDocuments}
            >
              {loadingDocuments ? (
                <svg className="animate-spin w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
              {loadingDocuments ? 'Loading...' : 'Refresh Documents'}
            </button>
          </div>
        </div>

        {loadingDocuments ? (
          <div className="text-center py-12">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg className="animate-spin w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <p className="text-gray-600">Loading documents...</p>
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <DocumentTextIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No documents uploaded yet</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              Upload your first document using the form above. Supported formats: PDF, JPG, PNG, DOC, DOCX, TXT
            </p>
          </div>
        ) : (
          <>
            <div className="mb-4 text-sm text-gray-600">
              Click on any document to download or delete.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-200 overflow-hidden group"
                >
                  <div className="p-5">
                    {/* Document Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3">
                          <span className="text-xl">{getDocumentIcon(doc.mime_type)}</span>
                        </div>
                        <div>
                          <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            {getDocumentTypeLabel(doc.document_type)}
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 flex items-center">
                        <CalendarDaysIcon className="w-3 h-3 mr-1" />
                        {doc.created_at ? formatDate(doc.created_at) : 'N/A'}
                      </div>
                    </div>

                    {/* Document Info */}
                    <div className="mb-4">
                      <h5 className="font-medium text-gray-900 mb-1 truncate">
                        {doc.description || doc.file_name}
                      </h5>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center truncate mr-2">
                          <PaperClipIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                          <span className="truncate">{doc.file_name}</span>
                        </div>
                        <button 
                          onClick={() => handleView(doc)}
                          className="text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap flex items-center"
                        >
                          View File
                        </button>
                      </div>
                    </div>

                    {/* Document Stats */}
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center">
                        <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                          {doc.mime_type.split('/')[1]?.toUpperCase() || 'FILE'}
                        </span>
                        <span className="mx-2">•</span>
                        <span>{formatFileSize(doc.file_size)}</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleDownload(doc)}
                        className="flex-1 bg-blue-50 text-blue-700 py-2 px-3 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-1 transition-colors flex items-center justify-center text-sm font-medium"
                      >
                        <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                        Download
                      </button>
                      {can('documents', 'write') && (
                        <button
                          onClick={() => doc.id && handleDelete(doc.id)}
                          disabled={isDeleting === doc.id}
                          className="flex-1 bg-red-50 text-red-600 py-2 px-3 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm font-medium"
                        >
                          {isDeleting === doc.id ? (
                            <>
                              <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Deleting...
                            </>
                          ) : (
                            <>
                              <TrashIcon className="w-4 h-4 mr-2" />
                              Delete
                            </>
                          )}
                        </button>
                      )}
                    </div>

                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

    </div>
  );
};

export default ClientPersonalDocuments;